/**
 * RafflePage — /raffle. All content is admin-managed (settings.raffle).
 * Countdown runs on SERVER time. The backend draws + saves the winner;
 * this page only reveals it (wheel spin for live viewers, gold winner
 * screen for everyone after).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Ticket, ShoppingCart, Loader2, Trophy, Users, CheckCircle2, PauseCircle } from "lucide-react";
import SubPageShell from "@/components/SubPageShell";
import RaffleWheel from "@/components/RaffleWheel";
import { fetchRaffle, fetchRaffleMe, tebexCheckoutRaffle } from "@/api";
import { useCfxAuth } from "@/context/CfxAuthContext";

const parseIso = (s) => {
  if (!s) return null;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : null;
};

function GoldConfetti() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const setSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setSize();
    window.addEventListener("resize", setSize);
    const colors = ["#f5e08c", "#ffffff", "#e6b84c", "#fff8e1", "#d4a437"];
    const parts = Array.from({ length: 140 }, () => ({
      x: Math.random() * window.innerWidth,
      y: -20 - Math.random() * window.innerHeight,
      w: 5 + Math.random() * 7,
      h: 8 + Math.random() * 8,
      vy: 1.6 + Math.random() * 2.6,
      vx: (Math.random() - 0.5) * 1.2,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.12,
      c: colors[Math.floor(Math.random() * colors.length)],
    }));
    let raf;
    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of parts) {
        p.y += p.vy;
        p.x += p.vx + Math.sin(p.y / 50) * 0.6;
        p.rot += p.vr;
        if (p.y > canvas.height + 20) {
          p.y = -20;
          p.x = Math.random() * canvas.width;
        }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.c;
        ctx.globalAlpha = 0.9;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", setSize);
    };
  }, []);
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[60]"
      data-testid="raffle-confetti"
    />
  );
}

const CountBox = ({ value, label }) => (
  <div className="flex flex-col items-center bg-[var(--theme-surface)]/80 border border-[var(--theme-border)] rounded-2xl px-4 py-3 min-w-[76px]">
    <span className="font-display text-4xl md:text-5xl tabular-nums leading-none">{String(value).padStart(2, "0")}</span>
    <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[var(--theme-text-muted)] mt-1">{label}</span>
  </div>
);

function Countdown({ targetMs, nowMs, label }) {
  const d = Math.max(0, targetMs - nowMs);
  const days = Math.floor(d / 86400000);
  const hours = Math.floor((d % 86400000) / 3600000);
  const mins = Math.floor((d % 3600000) / 60000);
  const secs = Math.floor((d % 60000) / 1000);
  return (
    <div data-testid="raffle-countdown">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--theme-text-muted)] mb-3">{label}</div>
      <div className="flex gap-2.5 flex-wrap">
        <CountBox value={days} label="Days" />
        <CountBox value={hours} label="Hours" />
        <CountBox value={mins} label="Mins" />
        <CountBox value={secs} label="Secs" />
      </div>
    </div>
  );
}

function WinnerScreen({ data }) {
  const w = data.winner || {};
  const drawDate = w.drawn_at ? new Date(w.drawn_at).toLocaleString() : "";
  return (
    <div className="relative min-h-[70vh] flex items-center justify-center px-6 py-24" data-testid="raffle-winner-screen">
      <GoldConfetti />
      <div className="nm-winner-in relative z-10 text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.4em] text-[#e6b84c] mb-6">
          <Trophy size={14} /> WINNER ANNOUNCEMENT
        </div>
        <h1 className="font-display uppercase text-2xl md:text-3xl tracking-[0.2em] text-[var(--theme-text-muted)] mb-4">
          {data.title || "RAFFLE"}
        </h1>
        {w.username ? (
          <>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--theme-text-muted)] mb-3">
              Congratulations to
            </div>
            <div
              data-testid="raffle-winner-username"
              className="nm-gold-text font-display uppercase text-[14vw] md:text-8xl lg:text-9xl leading-[0.9] break-words"
            >
              {w.username}
            </div>
          </>
        ) : (
          <div data-testid="raffle-no-winner" className="font-display uppercase text-5xl md:text-6xl text-[var(--theme-text-muted)]">
            No valid entries
          </div>
        )}
        <div className="nm-gold-ring mt-10 inline-block rounded-2xl border border-[#e6b84c]/40 bg-black/60 backdrop-blur px-8 py-6 text-left space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.26em] text-[#e6b84c]">Prize Won</div>
          <div className="font-display uppercase text-2xl md:text-3xl" data-testid="raffle-winner-prize">
            {w.prize_title || data.prize_title || "Grand Prize"}
          </div>
          {drawDate && (
            <div className="font-mono text-[11px] text-[var(--theme-text-muted)] tracking-wide" data-testid="raffle-winner-drawdate">
              Drawn {drawDate} — {w.total_entries || 0} entries
            </div>
          )}
        </div>
        {w.username && (
          <p className="mt-8 text-sm md:text-base text-[var(--theme-text-muted)] max-w-xl mx-auto leading-relaxed">
            {data.prize_description || "The winner will work directly with Null Modifications to create a completely unique vehicle personalized to them."}
          </p>
        )}
      </div>
    </div>
  );
}

function RaffleContent() {
  const { user, login } = useCfxAuth();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [offset, setOffset] = useState(0);
  const [nowMs, setNowMs] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const wasLiveRef = useRef(false);
  const [imgIdx, setImgIdx] = useState(0);

  const load = useCallback(async () => {
    try {
      const d = await fetchRaffle();
      setData(d);
      if (d.server_now) setOffset(new Date(d.server_now).getTime() - Date.now());
    } catch (_) { /* keep last data */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Per-user entry usage (only when logged in). Refetches whenever the raffle_id
  // rotates so the cap resets naturally.
  const loadMe = useCallback(async () => {
    if (!user) { setMe(null); return; }
    try { setMe(await fetchRaffleMe()); }
    catch (_) { setMe(null); }
  }, [user]);
  useEffect(() => { loadMe(); }, [loadMe, data?.status, data?.total_entries]);

  // 1s clock (server-synced)
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now() + offset), 1000);
    return () => clearInterval(id);
  }, [offset]);

  const status = data?.status || "hidden";
  const drawAt = parseIso(data?.draw_at);
  const startAt = parseIso(data?.start_at);
  const started = !startAt || nowMs >= startAt;
  const drawReached = !!drawAt && nowMs >= drawAt;
  const awaitingDraw = status === "active" && drawReached && !data?.winner;
  const entriesOpen = status === "active" && started && !drawReached;

  // Per-user cap (1 by default) — enforced server-side. UI reflects it so the
  // user never sees an error after clicking Enter.
  const cap = data?.entries_cap_per_user ?? 1;
  const used = me?.used ?? 0;
  const remaining = me ? Math.max(0, (me.remaining ?? cap - used)) : cap;
  const atCap = !!user && remaining <= 0;

  // Poll: fast while awaiting the draw result, slow otherwise
  useEffect(() => {
    if (status === "hidden") return;
    if (status === "completed" && revealed) return;
    const id = setInterval(load, awaitingDraw ? 3000 : 20000);
    return () => clearInterval(id);
  }, [load, awaitingDraw, status, revealed]);

  // Track live viewership so we know whether to animate the reveal
  useEffect(() => {
    if (status === "active" && !drawReached) wasLiveRef.current = true;
  }, [status, drawReached]);

  // Reveal logic
  useEffect(() => {
    if (!data?.winner) return;
    if (revealed || spinning) return;
    if (wasLiveRef.current && (data.entrants || []).length > 0 && data.winner.winner_index >= 0) {
      setSpinning(true);
    } else {
      setRevealed(true);
    }
  }, [data, revealed, spinning]);

  const enter = async () => {
    if (submitting) return;
    if (!user) {
      toast.error("Please log in with Cfx.re to enter");
      login();
      return;
    }
    setSubmitting(true);
    try {
      const res = await tebexCheckoutRaffle(1);
      const next = res.auth_url || res.checkout_url;
      if (next) {
        toast.success(res.auth_url ? "Linking your FiveM account on Tebex..." : "Redirecting to Tebex checkout...");
        // Best-effort refresh so cap reflects the just-created pending order
        // if the user comes back without paying.
        loadMe().catch(() => {});
        window.location.href = next;
      } else {
        toast.error("Tebex did not return a redirect URL");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Entry checkout failed");
      // Reload cap in case another tab already used entries
      loadMe().catch(() => {});
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center font-mono text-[10px] tracking-[0.4em] uppercase text-[var(--theme-text-muted)]">
        Loading
      </div>
    );
  }

  if (status === "hidden") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="raffle-hidden">
        <div className="text-center">
          <Ticket size={40} className="mx-auto text-[var(--theme-text-muted)] mb-4" />
          <div className="font-display uppercase text-3xl">No active raffle</div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--theme-text-muted)] mt-2">
            Check back soon
          </p>
        </div>
      </div>
    );
  }

  if (status === "completed" && revealed) {
    return <WinnerScreen data={data} />;
  }

  const images = data.prize_images || [];
  const mainImg = images[imgIdx] || images[0] || "";
  const entrants = data.entrants || [];

  return (
    <div className="max-w-[100rem] mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="raffle-page-content">
      {/* Header */}
      <div className="mb-12 md:mb-16">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--theme-text-muted)]">// GIVEAWAY</span>
          {status === "active" && <span className="nm-live-badge !text-[9px] !px-2.5 !py-1" data-testid="raffle-live-badge">LIVE</span>}
          {status === "paused" && (
            <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-amber-300 border border-amber-400/40 bg-amber-500/10 rounded-full px-2.5 py-1" data-testid="raffle-paused-badge">
              <PauseCircle size={11} /> PAUSED
            </span>
          )}
        </div>
        <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.9]" data-testid="raffle-title">
          {data.title}
        </h1>
        {data.description && (
          <p className="mt-5 text-[var(--theme-text-muted)] max-w-2xl text-base md:text-lg leading-relaxed">{data.description}</p>
        )}
      </div>

      {/* Prize + Entry */}
      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-8 mb-16">
        {/* Prize card */}
        <div className="nm-card p-6 md:p-8" data-testid="raffle-prize-card">
          {mainImg && (
            <div className="rounded-xl overflow-hidden border border-[var(--theme-border)] mb-4 bg-black">
              <img src={mainImg} alt={data.prize_title} className="w-full aspect-video object-cover" data-testid="raffle-prize-image" />
            </div>
          )}
          {images.length > 1 && (
            <div className="flex gap-2 mb-5 flex-wrap">
              {images.map((u, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  data-testid={`raffle-prize-thumb-${i}`}
                  className={`w-20 h-14 rounded-lg overflow-hidden border transition ${i === imgIdx ? "border-[var(--theme-accent-primary)]" : "border-[var(--theme-border)] opacity-60 hover:opacity-100"}`}
                >
                  <img src={u} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <h2 className="font-display uppercase text-3xl md:text-4xl" data-testid="raffle-prize-title">{data.prize_title}</h2>
            {data.prize_value && (
              <span className="font-display text-2xl md:text-3xl text-[#e6b84c]" data-testid="raffle-prize-value">
                {data.prize_value} VALUE
              </span>
            )}
          </div>
          {data.prize_description && (
            <p className="mt-3 text-sm md:text-base text-[var(--theme-text-muted)] leading-relaxed">{data.prize_description}</p>
          )}
          {(data.prize_features || []).length > 0 && (
            <ul className="mt-6 grid sm:grid-cols-2 gap-2.5" data-testid="raffle-prize-features">
              {data.prize_features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 size={16} className="text-[var(--theme-accent-primary)] mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Entry / countdown card */}
        <div className="nm-card p-6 md:p-8 flex flex-col gap-7 self-start" data-testid="raffle-entry-card">
          {startAt && !started ? (
            <Countdown targetMs={startAt} nowMs={nowMs} label="RAFFLE STARTS IN" />
          ) : drawAt ? (
            <Countdown targetMs={drawAt} nowMs={nowMs} label={drawReached ? "DRAWING NOW" : "DRAW IN"} />
          ) : null}

          <div className="flex items-center gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.26em] text-[var(--theme-text-muted)] mb-1">Entry Price</div>
              <div className="font-display text-3xl" data-testid="raffle-entry-price">{data.entry_price || "—"}</div>
            </div>
            <div className="w-px h-10 bg-[var(--theme-border)]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.26em] text-[var(--theme-text-muted)] mb-1">Valid Entries</div>
              <div className="font-display text-3xl flex items-center gap-2" data-testid="raffle-total-entries">
                <Users size={18} className="text-[var(--theme-text-muted)]" />
                {data.total_entries}
              </div>
            </div>
          </div>

          {entriesOpen ? (
            <div className="space-y-3">
              {user && me && (
                <div
                  data-testid="raffle-user-cap-info"
                  className={`font-mono text-[10px] uppercase tracking-[0.2em] leading-relaxed ${atCap ? "text-emerald-400" : "text-[var(--theme-text-muted)]"}`}
                >
                  {atCap
                    ? `// You're entered! Good luck — winner drawn when the timer hits zero.`
                    : `// 1 entry per CFX account · You have not entered yet`}
                </div>
              )}
              <button
                onClick={enter}
                disabled={submitting || !data.has_package || atCap}
                data-testid="raffle-enter-btn"
                className="btn-mega w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />}
                {submitting
                  ? "PROCESSING..."
                  : atCap
                    ? "ALREADY ENTERED"
                    : "BUY 1 ENTRY"}
              </button>
              <p className="font-mono text-[9px] text-[var(--theme-text-muted)] uppercase tracking-[0.18em] leading-relaxed">
                // Payment via Tebex · 1 entry per CFX account per raffle
              </p>
            </div>
          ) : (
            <div
              data-testid="raffle-entries-closed"
              className="rounded-xl border border-[var(--theme-border)] bg-black/50 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--theme-text-muted)] text-center"
            >
              {status === "paused" ? "Entries paused" : drawReached ? "Entries closed — drawing winner" : "Entries not open yet"}
            </div>
          )}
        </div>
      </div>

      {/* Wheel + entrants */}
      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-8 mb-16 items-start">
        <div className="nm-card p-6 md:p-10 flex flex-col items-center text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--theme-text-muted)] mb-6">
            {awaitingDraw || spinning ? "// DRAWING THE WINNER" : "// THE WHEEL"}
          </div>
          <RaffleWheel
            entrants={entrants}
            winnerIndex={data.winner ? data.winner.winner_index : null}
            spinning={spinning}
            onSpinComplete={() => setRevealed(true)}
          />
          {awaitingDraw && !spinning && (
            <div className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-[#e6b84c]" data-testid="raffle-drawing-indicator">
              <Loader2 size={14} className="animate-spin" /> Locking entries & selecting winner...
            </div>
          )}
        </div>

        <div className="nm-card p-6 md:p-8 self-start" data-testid="raffle-entrants">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--theme-text-muted)]">// ENTRANTS</div>
            <span className="font-display text-xl">{entrants.length}</span>
          </div>
          {entrants.length === 0 ? (
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--theme-text-muted)] py-8 text-center">
              No entries yet — be the first
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-[420px] overflow-y-auto pr-1">
              {entrants.map((u, i) => (
                <span
                  key={i}
                  data-testid={`raffle-entrant-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-black/50 px-3 py-1.5 text-xs"
                >
                  <span className="font-mono text-[9px] text-[var(--theme-text-muted)]">#{String(i + 1).padStart(2, "0")}</span>
                  {u}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rules */}
      {(data.rules || []).length > 0 && (
        <div className="nm-card p-6 md:p-8 max-w-4xl" data-testid="raffle-rules">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--theme-text-muted)] mb-5">// RAFFLE RULES</div>
          <ol className="space-y-3">
            {data.rules.map((r, i) => (
              <li key={i} className="flex gap-3 text-sm text-[var(--theme-text-muted)] leading-relaxed">
                <span className="font-mono text-[var(--theme-accent-primary)] text-xs pt-0.5">{String(i + 1).padStart(2, "0")}.</span>
                <span>{r}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export default function RafflePage() {
  return (
    <SubPageShell testId="raffle-page">
      {() => <RaffleContent />}
    </SubPageShell>
  );
}
